import { colorSet, domain_backend } from "const";
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

dayjs.extend(isSameOrBefore);

const NOW = dayjs();

const setFunction = {};

function removeVietnameseAccents(str) {
    if (!str) return "";
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, "");
}

function correct_media(path) {
    // Dev: iframe must be same-origin as the LMS so SCORM can access parent.API / API_1484_11.
    // Forwarding is done in src/setupProxy.js (package.json proxy skips GET + text/html).
    const mediaOrigin =
        process.env.NODE_ENV === "development"
            ? window.location.origin
            : new URL(domain_backend).origin;
    try {
        const url = new URL(path);
        const rel = url.pathname.startsWith("/")
            ? url.pathname.slice(1)
            : url.pathname;
        return `${mediaOrigin}/${rel}${url.search}${url.hash}`;
    } catch (TypeError) {
        const rel = path.startsWith("/") ? path.slice(1) : path;
        return `${mediaOrigin}/${rel}`;
    }
}

function downloadFile(path, file_name="") {
    const a = document.createElement('a');
    a.href = path;
    a.download = file_name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

function setCookie(cname, cvalue, exdays) {
    const d = new Date();
    d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
    let expires = "expires=" + d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/;";
}

function removeCookie(cname) {
    setCookie(cname, "")
}

function getCookie(cname) {
    let name = cname + "=";
    let ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
}

function createFormData(data, file_name) {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
        if (value) {
            if (key === "file" && file_name) {
                formData.append(key, value, file_name);
                continue;
            }
            formData.append(key, value);
        }
    }
    return formData;
}

function sortObjectKeys(obj) {
    return Object.keys(obj).sort().reduce((acc, key) => {
        acc[key] = obj[key];
        return acc;
    }, {});
}

function getAllDaysInMonth(date) {
    const start = date.clone().startOf('month');
    const end = date.clone().endOf('month');
    const days = [];

    let current = start;

    while (current.isSameOrBefore(end, 'day')) {
        days.push(current);
        current = current.add(1, 'day');
    }

    return days;
}

function formatTime(time, text = true) {
    const min = Math.floor(time / 60);
    var sec = time % 60;
    sec = String(sec).padStart(2, '0');
    if (text) return `${min} phút ${sec} giây`
    return `${min}:${sec}`
}

function formatScore(score, end_time=null, only_score=false) {
    if (end_time !== null) {
        if (score === null) {
            if (end_time.isAfter(NOW)) {
                return "Chưa làm bài"
            } else {
                if (only_score) {
                    return <span style={{ color: colorSet["low"] }}>
                        0
                    </span>;
                }
                return <span style={{ color: colorSet["low"] }}>Không làm bài</span>
            }
        }
        return (score / 100).toFixed(2)
    }
    if (score === null) return "Chưa nhập điểm"
    return (score / 100).toFixed(2)
}

function normalize(text) {
    return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

const courseStatusComponent = [
    <span className="test-status not-start">Chưa học</span>,
    <span className="test-status not-do">Đã rớt môn</span>,
    <span className="test-status during">Đang học</span>,
    <span className="test-status done">Đã qua môn</span>,
]

function considerCourseSTatusPart(item) {
    if (item.passed) {
        return 3;
    }
    if (item.end_time.isBefore(NOW)) {
        return 1;
    }
    return 2;
}

function considerCourseStatus(items) {
    if (items.length === 0) {
        return 0;
    }
    return Math.max(...items.map(item => considerCourseSTatusPart(item)))
}

function considerCourseStatusComponent(items) {
    return courseStatusComponent[Math.max(items.map(item => considerCourseSTatusPart(item)))];
}

function searchItem(value, item, fields) {
    var v = removeVietnameseAccents(value.toLowerCase().trim());
    for (const field of fields) {
        console.log(field)
        if (removeVietnameseAccents(item[field]?.trim().toLowerCase()).includes(v)) {
            return true;
        }
    }
    return false
}

const exportToExcel = (data, fileName, headers) => {
    const formatted = data.map((item) =>
        headers.reduce((acc, key) => {
            acc[key] = item[key] ?? "";
            return acc;
        }, {})
    );

    const ws = XLSX.utils.json_to_sheet(formatted, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");
    const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });

    saveAs(new Blob([wbout], { type: "application/octet-stream" }), `${fileName}.xlsx`);
};

function splitTextAndLatex(input) {
    const regex = /(\$\$.*?\$\$|\$.*?\$)/gs;
    let result = [];
    let lastIndex = 0;

    let match;
    while ((match = regex.exec(input)) !== null) {
        if (match.index > lastIndex) {
            result.push({
                content: input.slice(lastIndex, match.index),
                type: "text",
            });
        }

        const rawLatex = match[0];
        let content = "";
        let type = "";

        if (rawLatex.startsWith("$$")) {
            content = rawLatex.slice(2, -2).trim();
            type = "latex-block";
        } else {
            content = rawLatex.slice(1, -1).trim();
            type = "latex-inline";
        }

        result.push({
            content: content,
            type: type,
        });

        lastIndex = regex.lastIndex;
    }

    if (lastIndex < input.length) {
        result.push({
            content: input.slice(lastIndex),
            type: "text",
        });
    }
    
    return result;
}

function formatVND(num) {
    const integer = Math.floor(num);
    return integer.toLocaleString('vi-VN');
}

function sum(arr) {
    return arr.reduce((partialSum, a) => partialSum + a, 0);
}

function compareArrays(arr1, arr2) {
  if (arr1.length !== arr2.length) return false;

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }
  return true;
}

function searchWithoutAccent(input, option) {
    return removeVietnameseAccents(option.label.toLowerCase()).indexOf(removeVietnameseAccents(input.toLowerCase())) >= 0
}

function isImage(path) {
  return /\.(jpg|jpeg|png|gif|bmp|webp|svg|tiff)$/i.test(path);
}

export {
    setCookie, removeCookie, getCookie, setFunction,
    correct_media, createFormData, sortObjectKeys,
    getAllDaysInMonth, formatTime, formatScore, normalize,
    considerCourseStatus, considerCourseStatusComponent,
    courseStatusComponent, searchItem, removeVietnameseAccents,
    exportToExcel, splitTextAndLatex, formatVND, sum, downloadFile,
    compareArrays, searchWithoutAccent, isImage
}